// ECHOLOOP credit-gated progression.
//
// The campaign is a linear sequence of levels (see levels.ts LEVELS). The first
// `freeLevels` are always free; the rest are grouped into chapters, each
// unlocked once with credits (earned by play / ads / welcome bonus, or bought
// later). All numbers come from the central /platform-config.json via the
// Platform SDK, so retuning is a one-file edit; the FALLBACK below is used only
// if that file is missing/invalid.

import { Platform, type ChapterConfig, type GameConfig, type UnlockResult } from './platform';

export const GAME_ID = 'echoloop';
export type Chapter = ChapterConfig;

// Mirrors hub/platform-config.json games.echoloop so the campaign still gates
// correctly before that file loads (or in local per-game dev). The live config
// overrides this once Platform.loadConfig() resolves. First 7 levels free; the
// rest grouped into four credit-unlocked chapters. (The 3D campaign has 35
// levels — its 30–34 fall under chapter_5, which gates from idx 25 onward.)
const FALLBACK: GameConfig = {
  freeLevels: 7,
  chapters: [
    { sku: 'chapter_2', from: 7, price: 20, title: 'CHAPTER II · CROSSINGS' },
    { sku: 'chapter_3', from: 14, price: 30, title: 'CHAPTER III · TRIALS' },
    { sku: 'chapter_4', from: 21, price: 40, title: 'CHAPTER IV · MASTERY' },
    { sku: 'chapter_5', from: 25, price: 50, title: 'CHAPTER V · ASCENSION' },
  ],
};

function cfg(): GameConfig {
  return Platform.gameConfig(GAME_ID, FALLBACK);
}

function fullSku(sku: string): string {
  return `${GAME_ID}:${sku}`;
}

export function getChapters(): Chapter[] {
  return cfg().chapters;
}

/** The chapter that gates `idx`, or null if the level is in the free tier. */
export function chapterForLevel(idx: number): Chapter | null {
  const c = cfg();
  if (idx < c.freeLevels) return null;
  let found: Chapter | null = null;
  for (const ch of c.chapters) {
    if (ch.from <= idx) found = ch;
  }
  return found;
}

export function isLevelUnlocked(idx: number): boolean {
  // Master switch: while unlockAll is on, the system runs but nothing is gated.
  if (Platform.flags.unlockAll) return true;
  const ch = chapterForLevel(idx);
  if (ch === null) return true;
  return Platform.hasEntitlement(fullSku(ch.sku));
}

export function unlockChapter(ch: Chapter): UnlockResult {
  return Platform.unlock(fullSku(ch.sku), ch.price);
}

/** Pay out the first-clear bonus for a level (no-op on replays). */
export function rewardLevelClear(idx: number): number {
  const amount = Platform.limits.creditsPerClear;
  return Platform.earnOnce(`${GAME_ID}:clear:${idx}`, amount) ? amount : 0;
}
